import React, { createContext, useContext, useEffect, useRef } from 'react'
import { NohmoRNTracker } from './tracker'
import { setAutoCaptureTracker } from './autocapture'
import type { NohmoRNConfig, NohmoRNContextValue } from './types'

const NohmoRNContext = createContext<NohmoRNContextValue>({
  send: () => undefined,
  trackScreenView: () => undefined,
  trackConversion: () => undefined,
  buildInviteLink: async () => '',
  linkUser: async () => undefined,
  registerPushToken: async () => undefined,
  setInstallReferrer: async () => undefined,
  getDeepLink: () => null,
  onDeepLink: () => () => undefined,
})

interface NohmoProviderProps {
  children: React.ReactNode
  projectId: string
  apiKey: string
  options?: Partial<Omit<NohmoRNConfig, 'projectId' | 'apiKey'>>
}

export function NohmoProvider({
  children,
  projectId,
  apiKey,
  options = {},
}: NohmoProviderProps) {
  const trackerRef = useRef<NohmoRNTracker | null>(null)
  type PendingLink = [string, string | undefined, Record<string, unknown> | undefined]
  const pendingLinksRef = useRef<PendingLink[]>([])
  // Deep-link listeners registered by children before the tracker exists (child
  // effects run before the provider's), replayed once the tracker is created.
  const pendingDeepLinkRef = useRef<((value: string) => void)[]>([])

  useEffect(() => {
    const tracker = new NohmoRNTracker({ projectId, apiKey, ...options })
    trackerRef.current = tracker

    const pending = pendingLinksRef.current.splice(0)
    if (pending.length) {
      for (const [userId, email, meta] of pending) {
        tracker.linkUser(userId, email, meta)
      }
    }
    for (const cb of pendingDeepLinkRef.current.splice(0)) tracker.onDeepLink(cb)

    tracker.init()
    setAutoCaptureTracker(tracker)

    return () => { tracker.destroy() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const send = (event: string, data: Record<string, unknown> = {}) => {
    trackerRef.current?.send(event, data)
  }

  const trackScreenView = (screenName: string) => {
    trackerRef.current?.trackScreenView(screenName)
  }

  const trackConversion = (slug: string, properties?: Record<string, unknown>) => {
    trackerRef.current?.trackConversion(slug, properties)
  }

  const buildInviteLink = async (opts?: { channel?: string; campaign?: string; source?: string }) => {
    return (await trackerRef.current?.buildInviteLink(opts)) ?? ''
  }

  const linkUser = async (userId: string, email?: string, meta?: Record<string, unknown>) => {
    if (!trackerRef.current) {
      pendingLinksRef.current.push([userId, email, meta])
      return
    }
    await trackerRef.current.linkUser(userId, email, meta)
  }

  const registerPushToken = async (token: string) => {
    await trackerRef.current?.registerPushToken(token)
  }

  const setInstallReferrer = async (referrerString: string) => {
    await trackerRef.current?.setInstallReferrer(referrerString)
  }

  const getDeepLink = () => trackerRef.current?.getDeepLink() ?? null

  const onDeepLink = (cb: (value: string) => void) => {
    if (trackerRef.current) return trackerRef.current.onDeepLink(cb)
    // Tracker not ready yet — buffer, and allow unsubscribing before it's created.
    pendingDeepLinkRef.current.push(cb)
    return () => {
      const i = pendingDeepLinkRef.current.indexOf(cb)
      if (i >= 0) pendingDeepLinkRef.current.splice(i, 1)
    }
  }

  return (
    <NohmoRNContext.Provider value={{ send, trackScreenView, trackConversion, buildInviteLink, linkUser, registerPushToken, setInstallReferrer, getDeepLink, onDeepLink }}>
      {children}
    </NohmoRNContext.Provider>
  )
}

export function useNohmo(): NohmoRNContextValue {
  return useContext(NohmoRNContext)
}
